import React, { useMemo, useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { getDisplayNumber } from '@shared/helpers/display/get-display-number.helper';
import {
  getLifecycleBarStripeBackground,
  getLifecycleBarStripePhase,
} from '@shared/helpers/display/lifecycle-bar-stripe.helpers';
import { CO2_STACK_REMAINDER_KEY } from '@ui/BarChart/helpers/bar-chart.helpers';
import { ISimpleBarChartValue } from '@ui/BarChart/SimpleBarChart';
import {
  barScaledCss,
  PROPOSALS_OVERVIEW_CO2_LABEL,
  PROPOSALS_OVERVIEW_CO2_LABEL_MIN_HEIGHT_RATIO,
  PROPOSALS_OVERVIEW_DIFF_CHIP,
} from '@features/proposals/proposals-overview-bar-layout.helpers';
import {
  isSimpleBarChartTooltipDataComplete,
  SimpleBarChartTooltipBody,
} from '@features/projects/charts/SimpleBarChartTooltip';
import { useCo2eVisible } from '@features/results/co2e-visible.hook';
import { KineticNumberDisplay } from '@ui/KineticNumberDisplay';
import { Icon } from '@ui/Icon';
import { mergeSx } from '@client/helpers/mui.helpers';
import {
  getProposalBarBorderSx,
  PROPOSAL_BAR_BORDER_WIDTH_PX,
} from './proposal-bar-border.styles';

const COST_BAR_WIDTH = 4;
const COST_BAR_GAP = 6;
// Reserved for future lifecycle C/D bands once the chart supports them.
const TOP_TINT_HEIGHT = 0;
const COST_LABEL_CLEARANCE = 24;

/**
 * When proportional CO₂ bar height would be invisible (around one pixel or less), render at least
 * this many CSS pixels. Cross-bar ratios are distorted only when floored; tooltip values stay exact.
 */
const MIN_CO2_BAR_DISPLAY_PX = 4;

const relativePositionSx = { position: 'relative' } as const;
const absolutePositionSx = { position: 'absolute' } as const;

const CO2_BAR_EDGE_INSET = 'inset 0 0 0 1px rgba(0,0,0,0.1)' as const;
const formatCompactNumber = getDisplayNumber.withOptions({
  notation: 'compact',
});

/** Static layout/hover styling per stacked CO₂ segment; `bottom`, `height`, `bgcolor`, `opacity` stay dynamic. */
const CO2_SEGMENT_STATIC_SX = {
  left: -1,
  right: -1,
  transition: 'opacity 0.15s ease',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '1px',
    backgroundColor: 'rgba(0,0,0,0.1)',
    pointerEvents: 'none',
    zIndex: 1,
  },
} as const;

/** CSS length — number is interpreted as px (legacy); string carries its own unit (e.g. cqw). */
export type CssLength = number | string;

const toBottomOffset = (value: CssLength): string | number =>
  typeof value === 'number' ? `${value}px` : value;

export type VerticalBarSegment = ISimpleBarChartValue & {
  color?: string;
  tooltip?: React.ReactNode;
};

type VerticalBarProps = {
  /** When true, fills the parent (which must have a defined height) and uses % heights. */
  fluid?: boolean;
  barWidth?: number;
  maxBarHeight?: number;
  /** CSS-driven height scale, used by the scroll-animated project overview. */
  maxBarHeightCss?: string;
  color: string;
  co2eChartData: ISimpleBarChartValue[];
  /** Custom colored segments, replacing lifecycle bands. Values share the CO₂ scale. */
  segments?: VerticalBarSegment[];
  co2ePerGFA: number;
  co2ePerGfaMax: number;
  /** Passive solid bar rendered behind this bar at the supplied CO₂ value. */
  backdropCo2ePerGFA?: number;
  backdropColor?: string;
  /** Passive cost bar rendered behind this bar at the baseline height. */
  backdropCostBarHeight?: number;
  costBarHeight?: number;
  costLabel?: string;
  /** Render the documented 2 px outer-color + 2 px inner-black borders. */
  showInnerBorders?: boolean;
  /** Outer-border color. Defaults to the bar's proposal color. */
  innerBorderColor?: string;
  /** Hide the proposal fill and lifecycle segments. */
  outlineOnly?: boolean;
  tooltipPlacement?: 'left' | 'right';
  diffValue?: number | null;
  /** White CO₂ label on the bar face (default 30px — Figma proposals overview). */
  co2LabelFontSize?: CssLength;
  /** Min bar height before the CO₂ label is shown (default 36px). */
  minBarHeightForCo2Label?: number;
  /** Distance from bar bottom to label center (default 22px). */
  co2LabelBottomPx?: CssLength;
  /** Show the white cloud icon before the CO₂ value. */
  showCo2LabelIcon?: boolean;
  /** Hide labels that cannot fit a narrow area-scaled bar. */
  showCo2Label?: boolean;
  /** Dim bar content over an opaque warm surface so lines behind it stay hidden. */
  dimmed?: boolean;
};

const MIN_CO2_BAR_DISPLAY_RATIO = 0.02;

const VerticalBar: React.FC<VerticalBarProps> = ({
  fluid = false,
  barWidth = 0,
  maxBarHeight = 0,
  maxBarHeightCss,
  color,
  co2eChartData,
  segments,
  co2ePerGFA,
  co2ePerGfaMax,
  backdropCo2ePerGFA,
  backdropColor,
  backdropCostBarHeight = 0,
  costBarHeight = 0,
  costLabel,
  showInnerBorders = false,
  innerBorderColor = color,
  outlineOnly = false,
  tooltipPlacement = 'right',
  diffValue,
  co2LabelFontSize,
  minBarHeightForCo2Label = PROPOSALS_OVERVIEW_CO2_LABEL.minBarHeightPx,
  co2LabelBottomPx,
  showCo2LabelIcon = false,
  showCo2Label: co2LabelEnabled = true,
  dimmed = false,
}) => {
  const [hoveredIndex, setHoveredIndex] = useState(-1);
  const co2eVisible = useCo2eVisible();

  const co2HeightRatio = co2ePerGfaMax > 0 ? co2ePerGFA / co2ePerGfaMax : 0;

  const co2eBarHeightProportional = !co2eVisible.isEnabled
    ? 0
    : fluid
      ? co2HeightRatio
      : co2ePerGfaMax > 0
        ? (co2ePerGFA / co2ePerGfaMax) * maxBarHeight
        : 0;

  const co2eBarHeightDisplay = !co2eVisible.isEnabled
    ? 0
    : fluid
      ? co2ePerGFA > 0
        ? Math.max(co2HeightRatio, MIN_CO2_BAR_DISPLAY_RATIO)
        : 0
      : co2ePerGFA > 0
        ? Math.max(co2eBarHeightProportional, MIN_CO2_BAR_DISPLAY_PX)
        : 0;

  const backdropHeightRatio =
    co2ePerGfaMax > 0 && backdropCo2ePerGFA
      ? backdropCo2ePerGFA / co2ePerGfaMax
      : 0;
  const backdropBarHeightDisplay = !co2eVisible.isEnabled
    ? 0
    : fluid
      ? backdropCo2ePerGFA && backdropCo2ePerGFA > 0
        ? Math.max(backdropHeightRatio, MIN_CO2_BAR_DISPLAY_RATIO)
        : 0
      : backdropCo2ePerGFA && backdropCo2ePerGFA > 0
        ? Math.max(backdropHeightRatio * maxBarHeight, MIN_CO2_BAR_DISPLAY_PX)
        : 0;

  const co2BarDisplayLifted = fluid
    ? co2ePerGFA > 0 && co2eBarHeightDisplay > co2HeightRatio + 1e-9
    : co2ePerGFA > 0 && co2eBarHeightDisplay > co2eBarHeightProportional + 1e-9;

  const innerBarHeight = fluid
    ? co2eBarHeightDisplay
    : Math.max(0, co2eBarHeightDisplay - TOP_TINT_HEIGHT);

  const co2eSegments = useMemo(() => {
    if (co2ePerGFA <= 0 || innerBarHeight <= 0) return [];

    const phaseSegments = (segments ?? co2eChartData).filter(
      (seg) => seg.value > 0,
    );
    const phaseSum = phaseSegments.reduce((acc, seg) => acc + seg.value, 0);
    const remainder = Math.max(0, co2ePerGFA - phaseSum);
    const stackDenominator = Math.max(co2ePerGFA, phaseSum, 1e-15);

    const extended: VerticalBarSegment[] =
      remainder > 1e-9
        ? [
            ...phaseSegments,
            {
              key: CO2_STACK_REMAINDER_KEY,
              value: remainder,
              tooltipData: {
                label: 'Other CO₂e',
                co2e: remainder,
                sek: 0,
              },
            },
          ]
        : phaseSegments;

    let accumulated = 0;
    return extended.map((seg) => {
      if (fluid || maxBarHeightCss) {
        const heightPercent = (seg.value / stackDenominator) * 100;
        const bottomPercent = accumulated;
        accumulated += heightPercent;
        return {
          ...seg,
          segHeight: heightPercent,
          segBottom: bottomPercent,
          usePercent: true as const,
        };
      }
      const height = (seg.value / stackDenominator) * innerBarHeight;
      const bottom = accumulated;
      accumulated += height;
      return {
        ...seg,
        segHeight: height,
        segBottom: bottom,
        usePercent: false as const,
      };
    });
  }, [
    co2eChartData,
    segments,
    co2ePerGFA,
    fluid,
    innerBarHeight,
    maxBarHeightCss,
  ]);

  // Single tooltip for the stack avoids flicker: segment mouseLeave used to clear hover before the next segment's mouseEnter.
  const stackedLifecycleTooltipTitle = useMemo(() => {
    const segment = co2eSegments[hoveredIndex];
    if (segment?.tooltip) return segment.tooltip;
    const td = segment?.tooltipData;
    if (!td || !isSimpleBarChartTooltipDataComplete(td)) return '';
    return (
      <SimpleBarChartTooltipBody label={td.label} co2e={td.co2e} sek={td.sek} />
    );
  }, [co2eSegments, hoveredIndex]);

  const stackTooltipTitle = useMemo(() => {
    const phaseBody = stackedLifecycleTooltipTitle;
    if (!phaseBody && !co2BarDisplayLifted) return '';

    return (
      <>
        {phaseBody}
        {co2BarDisplayLifted && (
          <Typography
            component="span"
            variant="caption"
            sx={{
              display: 'block',
              mt: 0.5,
              opacity: 0.85,
            }}
          >
            Bar shown at minimum height for visibility; values are exact.
          </Typography>
        )}
      </>
    );
  }, [co2BarDisplayLifted, stackedLifecycleTooltipTitle]);

  const showCostBar = costBarHeight > 0;
  const showBackdropCostBar =
    backdropCostBarHeight > 0 && Boolean(backdropColor);
  const showCostColumn = showCostBar || showBackdropCostBar;
  const totalWidth =
    barWidth + (showCostColumn ? COST_BAR_GAP + COST_BAR_WIDTH : 0);

  const showCo2Label =
    co2LabelEnabled &&
    (fluid
      ? co2HeightRatio > PROPOSALS_OVERVIEW_CO2_LABEL_MIN_HEIGHT_RATIO
      : co2eBarHeightDisplay > minBarHeightForCo2Label);

  const co2BarHeightCss = fluid
    ? `${co2eBarHeightDisplay * 100}%`
    : maxBarHeightCss
      ? `max(${MIN_CO2_BAR_DISPLAY_PX}px, calc(${maxBarHeightCss} * ${co2HeightRatio}))`
      : co2eBarHeightDisplay;
  const costHeightCss = maxBarHeightCss
    ? `calc(${maxBarHeightCss} * ${maxBarHeight > 0 ? costBarHeight / maxBarHeight : 0})`
    : `${costBarHeight}px`;
  const backdropBarHeightCss = fluid
    ? `${backdropBarHeightDisplay * 100}%`
    : backdropBarHeightDisplay;
  const dimmedSurfaceBarHeight = Math.max(
    co2eBarHeightDisplay,
    backdropBarHeightDisplay,
  );
  const dimmedSurfaceBarHeightCss = fluid
    ? `${dimmedSurfaceBarHeight * 100}%`
    : dimmedSurfaceBarHeight;
  const dimmedSurfaceCostHeight = Math.max(
    costBarHeight,
    backdropCostBarHeight,
  );

  const resolvedCo2LabelFontSize = fluid
    ? (co2LabelFontSize ??
      barScaledCss(PROPOSALS_OVERVIEW_CO2_LABEL.fontSizePx))
    : (co2LabelFontSize ?? PROPOSALS_OVERVIEW_CO2_LABEL.fontSizePx);

  const resolvedCo2LabelBottom = fluid
    ? (co2LabelBottomPx ?? barScaledCss(PROPOSALS_OVERVIEW_CO2_LABEL.bottomPx))
    : (co2LabelBottomPx ?? PROPOSALS_OVERVIEW_CO2_LABEL.bottomPx);

  const rootSx = fluid
    ? { position: 'absolute' as const, inset: 0, flexShrink: 0 }
    : {
        width: totalWidth,
        height: maxBarHeightCss
          ? `calc(${maxBarHeightCss} + ${COST_LABEL_CLEARANCE}px)`
          : maxBarHeight + COST_LABEL_CLEARANCE,
        flexShrink: 0,
      };

  const co2BarWidthCss = fluid ? '100%' : barWidth;

  const barTopRadius = fluid
    ? `${barScaledCss(PROPOSALS_OVERVIEW_DIFF_CHIP.barTopRadiusPx)} ${barScaledCss(PROPOSALS_OVERVIEW_DIFF_CHIP.barTopRadiusPx)} 0 0`
    : '4px 4px 0 0';
  const innerBarTopRadius = fluid
    ? `max(0px, calc(${barScaledCss(PROPOSALS_OVERVIEW_DIFF_CHIP.barTopRadiusPx)} - ${PROPOSAL_BAR_BORDER_WIDTH_PX}px)) max(0px, calc(${barScaledCss(PROPOSALS_OVERVIEW_DIFF_CHIP.barTopRadiusPx)} - ${PROPOSAL_BAR_BORDER_WIDTH_PX}px)) 0 0`
    : `${PROPOSALS_OVERVIEW_DIFF_CHIP.barTopRadiusPx - PROPOSAL_BAR_BORDER_WIDTH_PX}px ${PROPOSALS_OVERVIEW_DIFF_CHIP.barTopRadiusPx - PROPOSAL_BAR_BORDER_WIDTH_PX}px 0 0`;

  const barEdgeInset = fluid
    ? `inset 0 0 0 ${barScaledCss(PROPOSALS_OVERVIEW_DIFF_CHIP.borderPx)} rgba(0,0,0,0.1)`
    : CO2_BAR_EDGE_INSET;

  const diffChipOver = Math.round(diffValue ?? 0) > 0;
  const diffChipPlate = {
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
    bgcolor: diffChipOver ? '#fbe7ea' : '#c8ffd6',
    // Chrome PDF paints this shadow as a gray rectangle behind the plate.
    '[data-report-print-sheet] &': { boxShadow: 'none' },
    '@media print': { boxShadow: 'none' },
  };
  const diffChipSx = fluid
    ? {
        bottom: `calc(${co2eBarHeightDisplay * 100}% + ${barScaledCss(PROPOSALS_OVERVIEW_DIFF_CHIP.gapAboveBarPx)})`,
        left: '50%',
        transform: 'translateX(-50%)',
        height: barScaledCss(PROPOSALS_OVERVIEW_DIFF_CHIP.heightPx),
        paddingLeft: barScaledCss(PROPOSALS_OVERVIEW_DIFF_CHIP.paddingXPx),
        paddingRight: barScaledCss(PROPOSALS_OVERVIEW_DIFF_CHIP.paddingXPx),
        borderRadius: barScaledCss(PROPOSALS_OVERVIEW_DIFF_CHIP.borderRadiusPx),
        display: 'flex',
        alignItems: 'center',
        whiteSpace: 'nowrap' as const,
        ...diffChipPlate,
        border: `${barScaledCss(PROPOSALS_OVERVIEW_DIFF_CHIP.borderPx)} solid rgba(0,0,0,0.1)`,
      }
    : {
        bottom: co2eBarHeightDisplay + 24,
        left: barWidth / 2,
        transform: 'translateX(-50%)',
        height: 24,
        px: '8px',
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'center',
        whiteSpace: 'nowrap' as const,
        ...diffChipPlate,
        border: '1px solid rgba(0,0,0,0.1)',
      };

  const diffLabelSx = fluid
    ? {
        fontWeight: 700,
        fontSize: barScaledCss(PROPOSALS_OVERVIEW_DIFF_CHIP.fontSizePx),
        letterSpacing: barScaledCss(
          PROPOSALS_OVERVIEW_DIFF_CHIP.letterSpacingPx,
        ),
        lineHeight: 1.5,
        color: Math.round(diffValue ?? 0) > 0 ? '#803943' : '#266b37',
        pointerEvents: 'none' as const,
      }
    : {
        fontWeight: 700,
        fontSize: 12,
        letterSpacing: '0.24px',
        lineHeight: 1.5,
        color: Math.round(diffValue ?? 0) > 0 ? '#803943' : '#266b37',
        pointerEvents: 'none' as const,
      };

  return (
    <Box sx={mergeSx(relativePositionSx, rootSx)}>
      {dimmed && dimmedSurfaceBarHeight > 0 && (
        <Box
          data-dimmed-bar-surface=""
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: co2BarWidthCss,
            height: dimmedSurfaceBarHeightCss,
            borderRadius: barTopRadius,
            bgcolor: 'warmSurface.dark',
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />
      )}
      {dimmed && showCostColumn && dimmedSurfaceCostHeight > 0 && (
        <Box
          data-dimmed-cost-surface=""
          sx={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            width: COST_BAR_WIDTH,
            height: dimmedSurfaceCostHeight,
            borderRadius: '1px 1px 0 0',
            bgcolor: 'warmSurface.dark',
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />
      )}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: dimmed ? 0.5 : 1,
          transition: 'opacity 150ms ease-in-out',
          zIndex: 1,
        }}
      >
        {/* Diff card — shown above CO2 bar top when target is set */}
        {diffValue != null && co2eBarHeightDisplay > 0 && (
          <Tooltip
            title={`${getDisplayNumber(Math.abs(diffValue))} kgCO2e per m² GFA ${
              diffValue > 0 ? 'above' : 'below'
            } target`}
            placement="right"
            disableInteractive
          >
            <Box
              data-drag-hide="true"
              sx={mergeSx(absolutePositionSx, diffChipSx, { zIndex: 3 })}
            >
              <Typography sx={diffLabelSx}>
                {Math.round(diffValue) !== 0 && (
                  <>{Math.round(diffValue) > 0 ? '+' : '−'} </>
                )}
                <KineticNumberDisplay
                  value={Math.abs(Math.round(diffValue))}
                  format={formatCompactNumber}
                />
              </Typography>
            </Box>
          </Tooltip>
        )}
        {backdropBarHeightDisplay > 0 && backdropColor && (
          <Box
            data-baseline-backdrop=""
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: co2BarWidthCss,
              height: backdropBarHeightCss,
              borderRadius: barTopRadius,
              bgcolor: backdropColor,
              boxShadow: barEdgeInset,
              pointerEvents: 'none',
              zIndex: 0,
            }}
          />
        )}
        {/* CO2 bar */}
        {co2eBarHeightDisplay > 0 && (
          <Box
            data-co2e-bar=""
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: co2BarWidthCss,
              height: co2BarHeightCss,
              borderRadius: barTopRadius,
              overflow: 'hidden',
              boxShadow: showInnerBorders ? 'none' : barEdgeInset,
              zIndex: 1,
              ...(showInnerBorders &&
                getProposalBarBorderSx(
                  innerBorderColor,
                  barTopRadius,
                  innerBarTopRadius,
                )),
            }}
          >
            {/* Full-height proposal tint over the baseline-gray backdrop. */}
            <Box
              data-proposal-tint=""
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: color,
                opacity: 0.2,
              }}
            />

            {/* Segments container — one Tooltip for whole stack (styled A1-A3 body); hover index updated per segment */}
            {!outlineOnly && innerBarHeight > 0 && (
              <Tooltip
                placement={tooltipPlacement}
                disableInteractive
                title={stackTooltipTitle}
              >
                <Box
                  onMouseLeave={() => setHoveredIndex(-1)}
                  sx={{
                    position: 'absolute',
                    top: fluid ? 0 : TOP_TINT_HEIGHT,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    overflow: 'hidden',
                    bgcolor: color,
                  }}
                >
                  {co2eSegments.map((seg, i) => {
                    const stripePhase =
                      !segments && seg.key !== CO2_STACK_REMAINDER_KEY
                        ? getLifecycleBarStripePhase(seg.key)
                        : undefined;
                    const isFaded = hoveredIndex >= 0 && hoveredIndex !== i;
                    const segmentPosition = seg.usePercent
                      ? {
                          bottom: `${seg.segBottom}%`,
                          height: `${seg.segHeight}%`,
                        }
                      : {
                          bottom: seg.segBottom,
                          height: seg.segHeight,
                        };
                    return (
                      <Box
                        key={seg.key}
                        onMouseEnter={() => setHoveredIndex(i)}
                        sx={{
                          position: 'absolute',
                          ...CO2_SEGMENT_STATIC_SX,
                          ...segmentPosition,
                          bgcolor: seg.color ?? color,
                          opacity: isFaded ? 0.5 : 1,
                        }}
                      >
                        {stripePhase && (
                          <Box
                            sx={{
                              position: 'absolute',
                              inset: 0,

                              backgroundImage:
                                getLifecycleBarStripeBackground(stripePhase),

                              opacity: 0.8,
                              pointerEvents: 'none',
                            }}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Tooltip>
            )}

            {/* CO2 value label — center at 18px from bar bottom, matching Figma */}
            {showCo2Label && (
              <Typography
                data-co2-label=""
                component="div"
                sx={{
                  position: 'absolute',
                  bottom: toBottomOffset(resolvedCo2LabelBottom),
                  left: '50%',
                  // translateY(50%) shifts element down by half height → center lands at offset from bottom
                  transform: 'translate(-50%, 50%)',
                  color: 'white',
                  fontWeight: 700,
                  fontSize: resolvedCo2LabelFontSize,
                  lineHeight: 1.5,
                  whiteSpace: 'nowrap',
                  opacity: 1,
                  pointerEvents: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  ml: showCo2LabelIcon ? '-8px' : 0,
                }}
              >
                {showCo2LabelIcon && (
                  <Box
                    component="span"
                    sx={{ display: 'flex', opacity: 0.9, flexShrink: 0 }}
                  >
                    <Icon category="costEmission" icon="kgCO2e" size={20} />
                  </Box>
                )}
                <Box component="span" data-co2-label-value="">
                  <KineticNumberDisplay
                    value={co2ePerGFA}
                    format={formatCompactNumber}
                  />
                </Box>
              </Typography>
            )}
          </Box>
        )}

        {showBackdropCostBar && (
          <Box
            data-baseline-cost-backdrop=""
            sx={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              width: COST_BAR_WIDTH,
              height: backdropCostBarHeight,
              bgcolor: backdropColor,
              borderRadius: '1px 1px 0 0',
              pointerEvents: 'none',
              zIndex: 0,
            }}
          />
        )}

        {/* Cost bar */}
        {showCostBar && (
          <Tooltip
            title={costLabel ?? ''}
            placement={tooltipPlacement}
            disableInteractive
          >
            <Box
              data-cost-bar=""
              sx={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                width: COST_BAR_WIDTH,
                height: costHeightCss,
                bgcolor: '#343434',
                borderRadius: '1px 1px 0 0',
                zIndex: 1,
              }}
            />
          </Tooltip>
        )}

        {/* Cost label — just above cost bar top, left edge aligned with cost bar left edge */}
        {costLabel && showCostBar && (
          <Box
            sx={{
              position: 'absolute',
              bottom: `calc(${costHeightCss} + 4px)`,
              left: barWidth + COST_BAR_GAP,
              fontSize: 12,
              color: 'rgba(0,0,0,0.6)',
              whiteSpace: 'nowrap',
              lineHeight: 1.5,
            }}
          >
            {costLabel}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default VerticalBar;
